import { Tables } from '@/types/database.types';
import { CustomQueryOptions } from '@/types/query';
import { useQuery, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBalanceActionHistory,
  getCredit,
  getCreditBalanceSummary,
  getInvoice,
  getUserBalance,
  getUserCurrentBalance,
  recalculateBalance,
} from '.';
import {
  GetBalanceActionHistoryParams,
  GetCreditRequest,
  GetCreditResponse,
  getCurrentBalnce,
  GetCreditBalanceSummaryResponse,
  GetInvoiceResponse,
} from './type';
import { PaginationResponse } from '@/types';
import { useDemoMode } from '@/context/DemoModeContext';
import { mockDelay } from '@/mock';
import { toast } from 'sonner';

const keyname = 'payment';

export const useGetUserBalance = (
  option?: CustomQueryOptions<Tables<'user_balance'>>
) => {
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: [`${keyname}-balance`, { isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(300);
        return {
          balance: 100,
          max_balance: 100,
          id: 0,
          last_updated: new Date().toISOString(),
          user_email: 'demo@example.com',
          user_id: 'demo-user',
        } as Tables<'user_balance'>;
      }
      return getUserBalance();
    },
    ...option,
  });
};

export const useGetUserCurrentBalance = (
  option?: CustomQueryOptions<getCurrentBalnce>
) => {
  return useQuery({
    queryKey: [`${keyname}-current-balance`],
    queryFn: getUserCurrentBalance,
    ...option,
  });
};

export const useGetgetBalanceActionHistory = (
  params: GetBalanceActionHistoryParams,
  option?: CustomQueryOptions<PaginationResponse<Tables<'balance_history'>>>
) => {
  return useQuery({
    queryKey: [[`${keyname}-balance-action`], { params }],
    queryFn: async () => getBalanceActionHistory(params),
    ...option,
  });
};

export const useGetInvoice = (
  option?: CustomQueryOptions<GetInvoiceResponse>
) => {
  return useQuery({
    queryKey: [`${keyname}-invoice`],
    queryFn: async () => getInvoice(),
    ...option,
  });
};

export const useGetCredit = (
  params: GetCreditRequest,
  option?: CustomQueryOptions<GetCreditResponse>
) => {
  return useQuery({
    queryKey: [`${keyname}-credit`, { params }],
    queryFn: async () => getCredit(params),
    ...option,
  });
};

// export const getSummaryCreditOption = () => {
//   return queryOptions({
//     queryKey: [`${keyname}-summary-credit`],
//     queryFn: async () => {
//       const { data: credit } = await getCredit({
//         limit: 10,
//       });
//       if (!credit) return 0;

//       const currentTime = dayjs().unix(); // Current time in Unix timestamp

//       return credit
//         .filter((item) => {
//           // Filter out voided credits
//           if (item.voided_at) return false;

//           // Filter out expired credits
//           if (item.expires_at && item.expires_at < currentTime) return false;

//           // Filter out credits that are not yet effective
//           if (item.effective_at && item.effective_at > currentTime)
//             return false;

//           return true;
//         })
//         .reduce((total, item) => {
//           // Convert from cents to dollars (following the pattern in credit/index.tsx)
//           return total + item.amount.monetary.value / 100;
//         }, 0);
//     },
//     staleTime: 1000 * 60 * 5,
//   });
// };

export const useGetCreditBalanceSummaryOption = () => {
  return queryOptions({
    queryKey: [`${keyname}-credit-balance-summary`],
    queryFn: async () => getCreditBalanceSummary(),
  });
};

export const useGetCreditBalanceSummary = (
  option?: CustomQueryOptions<GetCreditBalanceSummaryResponse>
) => {
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: [`${keyname}-credit-balance-summary`, { isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(300);
        return {
          object: 'billing.credit_balance_summary',
          balances: [
            {
              available_balance: {
                monetary: {
                  currency: 'usd',
                  value: 10000, // $100.00 in cents
                },
                type: 'monetary',
              },
              ledger_balance: {
                monetary: {
                  currency: 'usd',
                  value: 10000,
                },
                type: 'monetary',
              },
            },
          ],
          customer: 'demo-customer',
          livemode: false,
        } as GetCreditBalanceSummaryResponse;
      }
      return getCreditBalanceSummary();
    },
    ...option,
  });
};

/**
 * Hook to refresh user balance
 * 1. Calls backend to recalculate balance from transaction history
 * 2. After successful recalculation, invalidates queries to fetch updated balance from DB
 *
 * Backend endpoint: POST /balance/recalculate
 * See: BALANCE_RECALCULATE_SPEC.md for implementation details
 */
export const useRefreshBalance = () => {
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async () => {
      if (isDemoMode) {
        await mockDelay(500);
        return {
          success: true,
          balance: 100,
          previous_balance: 95,
          difference: 5,
          last_updated: new Date().toISOString(),
          transactions_processed: 42
        };
      }
      // Call backend endpoint to recalculate balance
      // This triggers full recalculation from balance_history table
      return recalculateBalance();
    },
    onSuccess: (data) => {
      // After backend recalculates, fetch updated balance from DB
      queryClient.invalidateQueries({ queryKey: [`${keyname}-balance`] });
      queryClient.invalidateQueries({ queryKey: [`${keyname}-current-balance`] });

      // Show detailed success message
      if (data.difference !== 0) {
        toast.success(
          `Balance updated: ${data.balance.toFixed(2)} (${data.difference > 0 ? '+' : ''}${data.difference.toFixed(2)})`
        );
      } else {
        toast.success('Balance verified: No changes needed');
      }
    },
    onError: (error) => {
      console.error('[RefreshBalance] Error:', error);
      toast.error('Failed to refresh balance. Please try again.');
    },
  });
};
